import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";

// The browser's own `title` tooltips (a plain grey box) are replaced on the
// Hive pages by a small window in the site's style. One listener for the
// whole page: when the mouse rests on an element with a `title` inside
// `.hive-root`, the text moves to `data-hive-title` (so the browser shows
// nothing) and this window shows it above the element (below it at the top
// of the screen), its notch pointing at the element. A tooltip of a league
// badge takes the badge's colours (the --hc / --hl / --hg it carries), and
// a text made of parts ("Diamond | R5 150%") shows them with diamonds
// between, the first part in the league colour. Touch screens never showed
// `title` tooltips, so neither does this.

const SHOW_DELAY_MS = 350;
const GAP_PX = 10;
const EDGE_PX = 8;

type Tip = {
  parts: string[];
  anchor: DOMRect;
  colors: CSSProperties;
};

const findTitled = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return null;
  }
  const element = target.closest<HTMLElement | SVGElement>(
    "[title], [data-hive-title]",
  );
  if (!element || !element.closest(".hive-root")) {
    return null;
  }
  const title = element.getAttribute("title");
  if (title !== null) {
    // keep it for the window (and for screen readers as a description)
    element.setAttribute("data-hive-title", title);
    element.setAttribute("aria-description", title);
    element.removeAttribute("title");
  }
  const text = element.getAttribute("data-hive-title")?.trim();
  return text ? { element, text } : null;
};

// the league colours of a badge, if the element is (inside) one
const readColors = (element: Element): CSSProperties => {
  const style = getComputedStyle(element);
  const text = style.getPropertyValue("--hc").trim();
  const line = style.getPropertyValue("--hl").trim();
  const glow = style.getPropertyValue("--hg").trim();
  if (!text || !line || !glow) {
    return {};
  }
  return {
    "--tip-text": text,
    "--tip-line": line,
    "--tip-glow": glow,
  } as CSSProperties;
};

export const HiveTooltips = () => {
  const [tip, setTip] = useState<Tip | null>(null);
  const [visible, setVisible] = useState(false);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState({
    left: 0,
    top: 0,
    notch: 0,
    below: false,
  });

  useEffect(() => {
    let current: Element | null = null;
    let timer = 0;

    const hide = () => {
      window.clearTimeout(timer);
      current = null;
      setVisible(false);
    };

    const handleOver = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") {
        return;
      }
      const found = findTitled(event.target);
      if (!found) {
        return;
      }
      if (found.element === current) {
        return;
      }
      window.clearTimeout(timer);
      const wasShowing = current !== null;
      current = found.element;
      const show = () => {
        if (current !== found.element) {
          return;
        }
        setTip({
          parts: found.text
            .split(" | ")
            .map((part) => part.trim())
            .filter(Boolean),
          anchor: found.element.getBoundingClientRect(),
          colors: readColors(found.element),
        });
        setVisible(true);
      };
      // moving from one titled element to the next shows it at once
      if (wasShowing) {
        show();
      } else {
        timer = window.setTimeout(show, SHOW_DELAY_MS);
      }
    };

    const handleOut = (event: PointerEvent) => {
      if (!current) {
        return;
      }
      const next = event.relatedTarget;
      if (next instanceof Node && current.contains(next)) {
        return;
      }
      hide();
    };

    document.addEventListener("pointerover", handleOver);
    document.addEventListener("pointerout", handleOut);
    document.addEventListener("pointerdown", hide, true);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("blur", hide);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("pointerover", handleOver);
      document.removeEventListener("pointerout", handleOut);
      document.removeEventListener("pointerdown", hide, true);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("blur", hide);
    };
  }, []);

  // above the element, centred on it and kept on the screen; below it when
  // there is no room above; the notch at the element's middle
  useLayoutEffect(() => {
    const element = tipRef.current;
    if (!tip || !element) {
      return;
    }
    const { width, height } = element.getBoundingClientRect();
    const { anchor } = tip;
    const middle = anchor.left + anchor.width / 2;
    const left = Math.min(
      Math.max(EDGE_PX, middle - width / 2),
      window.innerWidth - width - EDGE_PX,
    );
    const above = anchor.top - GAP_PX - height;
    const below = above < EDGE_PX;
    setPlacement({
      left,
      top: below ? anchor.bottom + GAP_PX : above,
      notch: Math.min(Math.max(12, middle - left), width - 12),
      below,
    });
  }, [tip]);

  if (!tip || typeof document === "undefined") {
    return null;
  }

  const [first, ...rest] = tip.parts;

  return createPortal(
    <div
      ref={tipRef}
      role="tooltip"
      className={[
        "hive-root hive-tooltip",
        visible ? "hive-tooltip-on" : "",
        placement.below ? "hive-tooltip-below" : "",
      ].join(" ")}
      style={
        {
          ...tip.colors,
          left: placement.left,
          top: placement.top,
          "--tip-notch": `${placement.notch}px`,
        } as CSSProperties
      }
    >
      <i className="hive-tooltip-notch" aria-hidden="true" />
      {rest.length > 0 ? (
        <>
          <span className="hive-tooltip-lead">{first}</span>
          {rest.map((part, index) => (
            <span key={index}>
              <i className="hive-tooltip-dia" aria-hidden="true" />
              {part}
            </span>
          ))}
        </>
      ) : (
        first
      )}
    </div>,
    document.body,
  );
};
